import React, { useEffect, useState, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
// only the navbar collapse plugin is used on the website
import "bootstrap/js/dist/collapse";
import ScrollToTop from "./ScrollToTop";
import RouteEffects from "./RouteEffects";
import ErrorBoundary from "./ErrorBoundary";
import { useLocation } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Components/Website/Css/Modern.css";
import "./Components/Website/Css/Home.css";
import {
  server_post_data,
  get_seo_data_all,
  Website_URL_admin_panel,
} from "./ServiceConnection/serviceconnection.js";

// Lazy-loaded pages
const Home = React.lazy(() => import("./Components/Website/Home"));
const About = React.lazy(() => import("./Components/Website/About"));
const Gallery = React.lazy(() => import("./Components/Website/Gallery"));
const Blog = React.lazy(() => import("./Components/Website/Blog"));
const CheckAvail = React.lazy(() => import("./Components/Website/CheckAvail"));
const PageNotFound = React.lazy(() => import("./Components/Website/404"));
const Events = React.lazy(() => import("./Components/Website/Events.js"));
const Testimonial = React.lazy(
  () => import("./Components/Website/Testimonials.js"),
);
const BlogMain = React.lazy(() => import("./Components/Website/BlogMain.js"));
const Termsofuse = React.lazy(() => import("./Components/Website/Termsofuse.js"));
const PrivacyPolicy = React.lazy(
  () => import("./Components/Website/PrivacyPolicy.js"),
);
const Contact = React.lazy(() => import("./Components/Website/Contact.js"));
const LeadForm = React.lazy(() => import("./Components/Website/LeadForm.js"));
const ThankYou = React.lazy(() => import("./Components/Website/Thankyou.js"));
const Preloader = React.lazy(() => import("./Loader.js"));

function RoutedBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function App() {
  const [SEOloop, setSEOloop] = useState([]);

  // Fetch SEO loop data
  const handleFetchData = async () => {
    try {
      const Response = await server_post_data(get_seo_data_all, null);
      if (!Response.data.error) setSEOloop(Response.data.message.seo_loop);
    } catch (error) {}
  };

  useEffect(() => {
    handleFetchData();
    AOS.init();
  }, []);

  // Redirect external admin panel URLs
  const RedirectToExternalURL = () => {
    window.location.href = Website_URL_admin_panel;
  };

  // Map SEO routes dynamically
  const match_and_return_seo_link_and_page = (
    call_function_name,
    pretty_function_name,
  ) => {
    switch (call_function_name) {
      case "/":
        return "Home~@~1";
      case "/about":
        return "AboutUs~@~2";
      case "/gallery":
        return "Gallery~@~3";
      case "/BookHall":
        return "CheckAvail~@~4";
      case "/events":
        return "Events~@~6";
      case "/testimonials":
        return "Testimonials~@~7";
      case "/contact":
        return "Contact~@~8";
      case "/privacypolicy":
        return "PrivacyPolicy~@~9";
      case "/termsofuse":
        return "TermsOfUse~@~10";
      case "/lead-form":
        return "LeadForm~@~11";
      default:
        if (call_function_name.includes("blog/blog_detail"))
          return pretty_function_name + "~@~5";
        return "";
    }
  };

  // Return component by page number
  const getPageByNumber = (page_number) => {
    switch (page_number) {
      case "1":
        return <Home />;
      case "2":
        return <About />;
      case "3":
        return <Gallery />;
      case "4":
        return <CheckAvail />;
      case "5":
        return <Blog />;
      case "6":
        return <Events />;
      case "7":
        return <Testimonial />;
      case "8":
        return <Contact />;
      case "9":
        return <PrivacyPolicy />;
      case "10":
        return <Termsofuse />;
      case "11":
        return <LeadForm />;
      default:
        return <PageNotFound />;
    }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <RouteEffects />
        <main id="main-content">
        <RoutedBoundary>
        <Suspense fallback={<Preloader />}>
          <Routes>
            {/* Static routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/BookHall" element={<CheckAvail />} />
            <Route path="/blog/blog_detail/:id" element={<Blog />} />
            <Route path="/blog" element={<BlogMain />} />
            <Route path="/termsofuse" element={<Termsofuse />} />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/events" element={<Events />} />
            <Route path="/testimonials" element={<Testimonial />} />
            <Route path="/lead-form" element={<LeadForm />} />
            <Route path="/thank-you" element={<ThankYou />} />

            {/* Dynamic SEO routes */}
            {SEOloop.map((data, index) => {
              const seoString = match_and_return_seo_link_and_page(
                data.call_function_name,
                data.pretty_function_name,
              );

              const [path, pageNumber] = seoString.split("~@~");
              if (!path) return null;

              return (
                <Route
                  key={index}
                  path={path}
                  element={getPageByNumber(pageNumber)}
                />
              );
            })}

            {/* Admin redirects */}
            {[
              "adminlogin*",
              "admindashboard",
              "blogDashboardView",
              "BlogDashboardAdd",
              "BlogDashboardEdit/:id",
              "seoDashboardView",
              "SeoDashboardAdd",
              "SeoDashboardEdit/:id",
              "imageDashboardView",
              "ImageDashboardAdd",
              "ImageDashboardEdit/:id",
              "menuDashboardView",
              "menuDashboardAdd",
              "menuDashboardEdit/:id",
              "testimonialDashboardView",
              "testimonialDashboardAdd",
              "testimonialDashboardEdit/:id",
              "teamDashboardView",
              "teamDashboardAdd",
              "teamDashboardEdit/:id",
              "websiteDashboardAdd",
              "subscribeDashboardView",
              "sliderDashboardView",
              "sliderDashboardAdd",
              "sliderDashboardEdit/:id",
              "hallDashboardView",
              "hallDashboardAdd",
              "hallDashboardEdit/:id",
              "eventTimingDashboardView",
              "eventTimingDashboardAdd",
              "eventTimingDashboardEdit/:id",
              "eventlistDashboardView",
              "eventlistDashboardAdd",
              "eventlistDashboardEdit/:id",
              "gallerywebsiteDashboardView",
              "gallerywebsiteDashboardAdd",
              "gallerywebsiteDashboardEdit/:id",
              "staffDashboardView",
              "staffDashboardAdd",
              "staffDashboardEdit/:id",
              "enquiryDashboardAdd",
              "enquiryDashboardView",
              "enquiryDashboardViewDetails/:id",
              "enquiryAssignDashboardView",
              "enquiryCompleteDashboardView",
              "CustomerBookingDashboardView",
              "enquiryDashboardDetails/:id",
              "enquiryDashboardAgain/:id",
              "enquiryCompleteDashboardDetails/:id",
              "EnquiryDashboardConfirm/:id",
              "CustomerBookingDashboardDetails/:id",
              "CustomerBookingInvoice/:id",
              "FeedbackDashboardView",
              "landmarkDashboardView",
              "landmarkDashboardAdd",
              "landmarkDashboardEdit/:id",
              "UsermasterDashboardView",
              "UsermasterDashboardAdd",
              "UsermasterDashboardEdit/:id",
              "UserMasterStaffDashboardEdit/:id",
            ].map((path, i) => (
              <Route
                key={i}
                path={`/${path}`}
                element={<RedirectToExternalURL />}
              />
            ))}

            {/* 404 */}
            <Route path="/*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
        </RoutedBoundary>
        </main>
      </BrowserRouter>
    </div>
  );
}

export default App;
